import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useIntl } from 'react-intl';

import errorHandler from 'src/utils/error-handler';
import LoadingContainer from 'src/componentes/loading-container';
import LucideIcon from 'src/componentes/lucide-icon';

import { getSharepointIcon } from '../../types';

const scopes = [
  'https://graph.microsoft.com/BrowserSiteLists.Read.All',
  'https://graph.microsoft.com/Files.Read.All',
  'https://graph.microsoft.com/Sites.Read.All',
];

const SharepointFileTree = ({ selectedFile, onSelectFile }) => {
  const { instance } = useMsal();
  const intl = useIntl();

  const [isLoading, setIsLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState([]);
  const [items, setItems] = useState([]);

  const getSitesData = async (accessToken) => {
    try {
      const response = await fetch(
        'https://graph.microsoft.com/v1.0/sites?$select=displayName,id,webUrl&search=*',
        {
          method: 'GET',
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      let data = await response.json();
      let responseValue = data?.value;
      if (!responseValue) return;

      while (data['@odata.nextLink']) {
        const nextResponse = await fetch(data['@odata.nextLink'], {
          method: 'GET',
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const nextData = await nextResponse.json();
        responseValue = [...responseValue, ...nextData.value];
        data = nextData;
      }

      const values = responseValue?.map((item) => {
        return {
          children: [],
          contentType: 'Site',
          extension: 'site',
          id: item.id,
          name: item.displayName,
          webUrl: item.webUrl,
        };
      });

      setItems(values);
      setIsLoading(false);

      return response.value;
    } catch (error) {
      setIsLoading(false);
      errorHandler(error);
    }
  };

  const getToken = async () => {
    setIsLoading(true);

    const activeAccount = instance.getActiveAccount();
    try {
      const { accessToken } = await instance.acquireTokenSilent({
        scopes: scopes,
        account: activeAccount,
      });

      await getSitesData(accessToken);
    } catch (error) {
      const { accessToken } = await instance.acquireTokenPopup({
        scopes: scopes,
      });

      await getSitesData(accessToken);
    }
  };

  useEffect(() => {
    getToken();
  }, []);

  const transformData = (inputData, webUrl) => {
    const folderMap = {}; // Para mapear IDs de pastas aos seus respectivos índices no array de saída

    // Função para adicionar um item a uma pasta ou subpasta
    const addItemToFolder = (folder, item) => {
      if (!folder.children) {
        folder.children = [];
      }

      const existingItem =
        folder.children &&
        folder.children.find((child) => child.id === item.id);
      if (!existingItem) {
        folder.children.push(item);
      }
    };

    // Organizar os itens em suas respectivas pastas
    inputData.forEach((item) => {
      const pathParts = item.path.split('/');
      let currentFolder = null;

      // Navegar até a pasta correspondente no mapa
      for (let i = 0; i < pathParts.length; i++) {
        const part = pathParts[i];
        if (part) {
          if (!currentFolder) {
            if (!folderMap[part]) {
              folderMap[part] = {
                id: item.id,
                contentType: item.contentType,
                name: part,
                extension: item.extension,
                webUrl: webUrl,
              };
            }
            currentFolder = folderMap[part];
          } else {
            const existingSubfolder =
              currentFolder.children &&
              currentFolder.children.find((child) => child.name === part);
            if (existingSubfolder) {
              currentFolder = existingSubfolder;
            } else {
              const newSubfolder = {
                id: item.id,
                contentType: item.contentType,
                name: part,
                extension: item.extension,
                webUrl: webUrl,
              };
              addItemToFolder(currentFolder, newSubfolder);
              currentFolder = newSubfolder;
            }
          }
        }
      }

      // Adicionar o item à pasta atual
      if (
        currentFolder &&
        (currentFolder.contentType === 'Folder' ||
          currentFolder.contentType === 'Pasta') &&
        (item.contentType === 'Document' || item.contentType === 'Documento')
      ) {
        addItemToFolder(currentFolder, {
          id: item.id,
          contentType: item.contentType,
          name: item.name,
          extension: item.extension,
          webUrl: webUrl,
        });
      }
    });

    // Encontrar as pastas de nível superior (root)
    const rootFolders = Object.values(folderMap).filter((folder) => {
      const pathParts = folder.name.split('/');
      return pathParts.length === 1;
    });

    return rootFolders;
  };

  const getIcon = (extension, childVisible) => {
    return getSharepointIcon(extension, childVisible);
  };

  const getLibrariesData = async (accessToken, siteId, webUrl) => {
    try {
      const response = await fetch(
        `https://graph.microsoft.com/v1.0/sites/${siteId}/lists?$select=displayName,list,id`,
        {
          method: 'GET',
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      let data = await response.json();
      let responseValue = data?.value;
      if (!responseValue) return;

      while (data['@odata.nextLink']) {
        const nextResponse = await fetch(data['@odata.nextLink'], {
          method: 'GET',
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const nextData = await nextResponse.json();
        responseValue = [...responseValue, ...nextData.value];
        data = nextData;
      }

      const filteredDocumentLibraries = responseValue?.filter(
        (item) => item.list.template === 'documentLibrary'
      );
      const values = filteredDocumentLibraries?.map((item) => {
        return {
          children: [],
          contentType: 'Library',
          extension: 'library',
          id: item.id,
          name: item.displayName,
          webUrl: webUrl,
          siteId: siteId,
        };
      });

      setIsLoading(false);

      return values;
    } catch (error) {
      setIsLoading(false);
      errorHandler(error);
    }
  };

  const handleExpandSite = async (node) => {
    setIsLoading(true);

    const activeAccount = instance.getActiveAccount();
    try {
      const { accessToken } = await instance.acquireTokenSilent({
        scopes: scopes,
        account: activeAccount,
      });

      return await getLibrariesData(accessToken, node.id, node.webUrl);
    } catch (error) {
      const { accessToken } = await instance.acquireTokenPopup({
        scopes: scopes,
      });

      return await getLibrariesData(accessToken, node.id, node.webUrl);
    }
  };

  const getFilesData = async (accessToken, libraryId, siteId, webUrl) => {
    try {
      const response = await fetch(
        `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${libraryId}/items?expand=fields(select=id,ContentType,FileLeafRef,DocIcon)&$select=id,name,webUrl`,
        {
          method: 'GET',
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      let data = await response.json();
      let responseValue = data?.value;
      if (!responseValue) return;

      while (data['@odata.nextLink']) {
        const nextResponse = await fetch(data['@odata.nextLink'], {
          method: 'GET',
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const nextData = await nextResponse.json();
        responseValue = [...responseValue, ...nextData.value];
        data = nextData;
      }

      const result = responseValue?.map((item) => {
        return {
          id: item.fields['@odata.etag'].split(',')[0].replace(/"/g, ''),
          path: decodeURIComponent(
            item.webUrl
              .replace(webUrl + '/', '')
              .split('/')
              .slice(1)
              .join('/')
          ),
          name: item.fields.FileLeafRef,
          extension: item.fields.DocIcon ?? 'folder',
          contentType: item.fields.ContentType,
        };
      });
      const finalData = transformData(result, webUrl);

      setIsLoading(false);

      return finalData;
    } catch (error) {
      setIsLoading(false);
      errorHandler(error);
    }
  };

  const handleExpandLibrary = async (node) => {
    setIsLoading(true);

    const activeAccount = instance.getActiveAccount();
    try {
      const { accessToken } = await instance.acquireTokenSilent({
        scopes: scopes,
        account: activeAccount,
      });

      return await getFilesData(accessToken, node.id, node.siteId, node.webUrl);
    } catch (error) {
      const { accessToken } = await instance.acquireTokenPopup({
        scopes: scopes,
      });

      return await getFilesData(accessToken, node.id, node.siteId, node.webUrl);
    }
  };

  const getChildren = async (node) => {
    const contentType = node.contentType;

    if (contentType === 'Site') return await handleExpandSite(node);

    if (contentType === 'Library') return await handleExpandLibrary(node);
  };

  const findById = (tree, nodeId) => {
    for (let node of tree) {
      if (node.id === nodeId) return node;

      if (node.children) {
        let desiredNode = findById(node.children, nodeId);
        if (desiredNode) return desiredNode;
      }
    }
    return null;
  };

  const handleToggleItem = (id) => {
    const newList = [...expandedIds];
    const index = newList.findIndex((i) => i === id);

    if (index === -1) {
      newList.push(id);
    } else {
      newList.splice(index, 1);
    }

    setExpandedIds(newList);
  };

  const handleExpandChildren = async (id) => {
    const newList = [...items];
    const node = findById(newList, id);

    const hasChildren = node.children?.length > 0;

    if (
      !hasChildren &&
      (node.contentType !== 'Folder' || node.contentType !== 'Pasta')
    ) {
      node.children = await getChildren(node);
      setItems(newList);
    }

    handleToggleItem(id);
  };

  const findByIdWithParents = (tree, nodeId, parents = []) => {
    for (let node of tree) {
      if (node.id === nodeId) {
        return { node, parents };
      }

      if (node.children) {
        let result = findByIdWithParents(node.children, nodeId, [
          ...parents,
          node,
        ]);
        if (result) return result;
      }
    }
    return null;
  };

  const findFilePathInTree = (treeArray, targetId) => {
    function traverse(node, currentPath) {
      if (node.id === targetId) {
        return currentPath + node.name;
      }

      if (node.children) {
        for (let i = 0; i < node.children.length; i++) {
          const path = traverse(
            node.children[i],
            currentPath + node.name + ' > '
          );
          if (path) {
            return path;
          }
        }
      }

      return null;
    }

    for (let i = 0; i < treeArray.length; i++) {
      const filePath = traverse(treeArray[i], '');
      if (filePath) {
        return filePath;
      }
    }

    return intl.formatMessage({ id: 'arquivoNaoEncontrado' });
  };

  const handleSelectFile = (id) => {
    const result = findByIdWithParents(items, id);

    if (!result) return;

    const { node, parents } = result;
    const filePath = findFilePathInTree(items, id);

    const library = parents.find((p) => p.contentType === 'Library');

    const nodeWithPath = {
      ...node,
      filePath: filePath,
      libraryId: library?.id || null,
      siteId: library?.siteId || null,
    };

    onSelectFile(nodeWithPath);
  };

  const renderItems = (items, level = 0) => {
    return (
      <ul className="d-flex d-tree-container flex-column">
        {items?.map((i) => {
          const isExpanded = expandedIds.includes(i.id);
          const isDocument =
            i.contentType === 'Document' || i.contentType === 'Documento';

          return (
            <>
              <li
                className="d-tree-node border-0"
                style={{
                  backgroundColor:
                    i.id === selectedFile?.id ? '#2377881A' : null,
                  cursor:
                    i.contentType === 'Document' ||
                    i.contentType === 'Documento'
                      ? 'pointer'
                      : 'default',
                  paddingLeft: level * 25,
                }}
              >
                <div
                  className="d-flex cursor-pointer"
                  onClick={() =>
                    i.contentType === 'Document' ||
                    i.contentType === 'Documento'
                      ? handleSelectFile(i.id)
                      : handleExpandChildren(i.id)
                  }
                >
                  {!isDocument && (
                    <div
                      className={`d-inline d-tree-toggler ${
                        isExpanded ? 'active' : ''
                      }`}
                    >
                      <LucideIcon
                        icon={isExpanded ? 'chevron-down' : 'chevron-right'}
                        size={16}
                      />
                    </div>
                  )}

                  <div className="d-tree-head d-flex align-items-center ml-1">
                    {i.contentType !== 'Site' &&
                      i.contentType !== 'Library' && (
                        <img
                          alt="Icon"
                          src={`/integracoes/${getIcon(
                            i.extension,
                            isExpanded
                          )}`}
                          style={{ width: 18, height: 18 }}
                        />
                      )}

                    <span
                      className={`ml-2 ${
                        !isDocument
                          ? 'text-primary font-weight-bolder'
                          : 'font-weight-bold'
                      }`}
                    >
                      {i.name}
                    </span>
                  </div>
                </div>
              </li>
              <hr className="w-100 my-1"></hr>
              {isExpanded && renderItems(i.children, level + 1)}
            </>
          );
        })}
      </ul>
    );
  };

  return (
    <LoadingContainer isLoading={isLoading}>
      <div className="my-4">{renderItems(items)}</div>
    </LoadingContainer>
  );
};

export default SharepointFileTree;
